import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import { Plus, Edit2, Power, AlertCircle, X, Package } from 'lucide-react';

interface SubscriptionPlan {
  id: string;
  plan_name: string;
  plan_type: 'free' | 'premium' | 'enterprise';
  price_vatu: number;
  billing_model?: 'flat' | 'per_member';
  price_per_member_vatu?: number | null;
  max_group_members: number | null;
  max_contributions_per_month: number | null;
  features: Record<string, any>;
  is_active: boolean;
  created_at: string;
}

const SubscriptionPlanManagement = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    plan_name: '',
    plan_type: 'premium' as 'free' | 'premium' | 'enterprise',
    price_vatu: 0,
    billing_model: 'flat' as 'flat' | 'per_member',
    price_per_member_vatu: 0,
    max_group_members: '',
    max_groups: '',
    max_contributions_per_month: '',
    features: [] as string[],
    reporting: 'basic',
    support: 'community',
    is_active: true
  });

  // Fetch subscription plans
  useEffect(() => {
    if (!user || !userProfile || userProfile.role !== 'super_admin') return;

    const fetchPlans = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase
          .from('subscription_plans')
          .select('*')
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        setPlans(data || []);

      } catch (err: any) {
        console.error('Error fetching subscription plans:', err);
        setError(err.message || 'Failed to load subscription plans');
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, [user, userProfile]);

  const handleCreatePlan = async () => {
    try {
      // Validate required fields
      if (!formData.plan_name.trim()) {
        alert('Plan name is required');
        return;
      }

      if (formData.billing_model === 'flat') {
        if (formData.price_vatu < 0) {
          alert('Price must be 0 or greater');
          return;
        }
      } else {
        if (formData.price_per_member_vatu < 0) {
          alert('Per-member price must be 0 or greater');
          return;
        }
      }

      const validFeatures = formData.features.filter(f => f.trim() !== '');
      if (validFeatures.length === 0) {
        alert('At least one feature is required');
        return;
      }

      // Structure features correctly for mobile app
      const featuresJson = {
        reporting: formData.reporting,
        support: formData.support,
        features: validFeatures
      };

      // Prepare insert data
      const insertData: any = {
        plan_name: formData.plan_name.trim(),
        plan_type: formData.plan_type,
        billing_model: formData.billing_model,
        price_vatu: formData.billing_model === 'flat' ? (Number(formData.price_vatu) || 0) : 0,
        price_per_member_vatu: formData.billing_model === 'per_member'
          ? (Number(formData.price_per_member_vatu) || 0)
          : null,
        features: featuresJson,
        is_active: formData.is_active
      };

      // Only add max_group_members if provided
      if (formData.max_group_members && formData.max_group_members.trim() !== '') {
        const maxMembers = parseInt(formData.max_group_members);
        if (!isNaN(maxMembers) && maxMembers > 0) {
          insertData.max_group_members = maxMembers;
        }
      }

      // Only add max_contributions_per_month if provided
      if (formData.max_contributions_per_month && formData.max_contributions_per_month.trim() !== '') {
        const maxContributions = parseInt(formData.max_contributions_per_month);
        if (!isNaN(maxContributions) && maxContributions > 0) {
          insertData.max_contributions_per_month = maxContributions;
        }
      }

      // Only add max_groups if provided
      if (formData.max_groups && formData.max_groups.trim() !== '') {
        const maxGroups = parseInt(formData.max_groups);
        if (!isNaN(maxGroups) && maxGroups > 0) {
          insertData.max_groups = maxGroups;
        }
      }

      console.log('Creating plan with data:', insertData);

      const { data, error: createError } = await supabase
        .from('subscription_plans')
        .insert(insertData)
        .select()
        .single();

      if (createError) {
        console.error('Supabase error:', createError);
        throw createError;
      }

      // Log audit (non-blocking - don't fail if audit fails)
      try {
        await supabase.rpc('create_audit_log', {
          p_action_type: 'plan_created',
          p_entity_type: 'subscription_plan',
          p_entity_id: data.id,
          p_details: { plan_name: formData.plan_name }
        });
      } catch (auditError) {
        console.warn('Failed to create audit log:', auditError);
        // Don't throw - plan creation succeeded
      }

      setPlans([data, ...plans]);
      setShowCreateModal(false);
      resetForm();
      alert('Subscription plan created successfully!');

    } catch (err: any) {
      console.error('Error creating plan:', err);
      const errorMessage = err.message || err.toString() || 'Unknown error occurred';
      alert(`Failed to create plan: ${errorMessage}`);
    }
  };

  const handleUpdatePlan = async () => {
    if (!editingPlan) return;

    try {
      // Validate required fields
      if (!formData.plan_name.trim()) {
        alert('Plan name is required');
        return;
      }

      if (formData.billing_model === 'flat') {
        if (formData.price_vatu < 0) {
          alert('Price must be 0 or greater');
          return;
        }
      } else {
        if (formData.price_per_member_vatu < 0) {
          alert('Per-member price must be 0 or greater');
          return;
        }
      }

      const validFeatures = formData.features.filter(f => f.trim() !== '');
      if (validFeatures.length === 0) {
        alert('At least one feature is required');
        return;
      }

      // Structure features correctly for mobile app
      const featuresJson = {
        reporting: formData.reporting,
        support: formData.support,
        features: validFeatures
      };

      // Prepare update data (don't update plan_type - it's disabled in the form)
      const updateData: any = {
        plan_name: formData.plan_name.trim(),
        billing_model: formData.billing_model,
        price_vatu: formData.billing_model === 'flat' ? (Number(formData.price_vatu) || 0) : 0,
        price_per_member_vatu: formData.billing_model === 'per_member'
          ? (Number(formData.price_per_member_vatu) || 0)
          : null,
        features: featuresJson,
        is_active: formData.is_active,
        updated_at: new Date().toISOString()
      };

      // Only add max_group_members if provided
      if (formData.max_group_members && formData.max_group_members.trim() !== '') {
        const maxMembers = parseInt(formData.max_group_members);
        if (!isNaN(maxMembers) && maxMembers > 0) {
          updateData.max_group_members = maxMembers;
        } else {
          updateData.max_group_members = null;
        }
      } else {
        updateData.max_group_members = null;
      }

      // Only add max_groups if provided
      if (formData.max_groups && formData.max_groups.trim() !== '') {
        const maxGroups = parseInt(formData.max_groups);
        if (!isNaN(maxGroups) && maxGroups > 0) {
          updateData.max_groups = maxGroups;
        } else {
          updateData.max_groups = null;
        }
      } else {
        updateData.max_groups = null;
      }

      // Only add max_contributions_per_month if provided
      if (formData.max_contributions_per_month && formData.max_contributions_per_month.trim() !== '') {
        const maxContributions = parseInt(formData.max_contributions_per_month);
        if (!isNaN(maxContributions) && maxContributions > 0) {
          updateData.max_contributions_per_month = maxContributions;
        } else {
          updateData.max_contributions_per_month = null;
        }
      } else {
        updateData.max_contributions_per_month = null;
      }

      console.log('Updating plan with data:', updateData);
      console.log('Plan ID:', editingPlan.id);

      // Try the update with better error handling
      let data, updateError;
      try {
        const result = await supabase
          .from('subscription_plans')
          .update(updateData)
          .eq('id', editingPlan.id)
          .select()
          .single();
        
        data = result.data;
        updateError = result.error;
      } catch (networkError: any) {
        console.error('Network error during update:', networkError);
        throw new Error(`Network error: ${networkError.message || 'Failed to connect to server'}`);
      }

      if (updateError) {
        console.error('Supabase error:', updateError);
        console.error('Error code:', updateError.code);
        console.error('Error details:', updateError.details);
        console.error('Error hint:', updateError.hint);
        throw new Error(updateError.message || `Update failed: ${updateError.code || 'Unknown error'}`);
      }

      if (!data) {
        throw new Error('Update succeeded but no data returned');
      }

      // Log audit (non-blocking - don't fail if audit fails)
      try {
        await supabase.rpc('create_audit_log', {
          p_action_type: 'plan_updated',
          p_entity_type: 'subscription_plan',
          p_entity_id: editingPlan.id,
          p_details: { plan_name: formData.plan_name }
        });
      } catch (auditError) {
        console.warn('Failed to create audit log:', auditError);
        // Don't throw - plan update succeeded
      }

      setPlans(plans.map(p => p.id === editingPlan.id ? data : p));
      setEditingPlan(null);
      resetForm();
      alert('Subscription plan updated successfully!');

    } catch (err: any) {
      console.error('Error updating plan:', err);
      const errorMessage = err.message || err.toString() || 'Unknown error occurred';
      alert(`Failed to update plan: ${errorMessage}`);
    }
  };

  const handleToggleActive = async (planId: string, currentStatus: boolean) => {
    try {
      const { error: toggleError } = await supabase
        .from('subscription_plans')
        .update({ is_active: !currentStatus })
        .eq('id', planId);

      if (toggleError) throw toggleError;

      // Log audit (non-blocking - don't fail if audit fails)
      try {
        await supabase.rpc('create_audit_log', {
          p_action_type: currentStatus ? 'plan_deleted' : 'plan_created',
          p_entity_type: 'subscription_plan',
          p_entity_id: planId,
          p_details: { is_active: !currentStatus }
        });
      } catch (auditError) {
        console.warn('Failed to create audit log:', auditError);
        // Don't throw - toggle succeeded
      }

      setPlans(plans.map(p => 
        p.id === planId ? { ...p, is_active: !currentStatus } : p
      ));

    } catch (err: any) {
      console.error('Error toggling plan status:', err);
      alert(`Failed to toggle plan status: ${err.message}`);
    }
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    const features = plan.features || {};
    const featuresArray = Array.isArray(features.features) 
      ? features.features 
      : (Array.isArray(features) ? features : []);
    
    setFormData({
      plan_name: plan.plan_name,
      plan_type: plan.plan_type,
      price_vatu: plan.price_vatu,
      billing_model: (plan.billing_model ?? 'flat') as 'flat' | 'per_member',
      price_per_member_vatu: plan.price_per_member_vatu ?? 0,
      max_group_members: plan.max_group_members?.toString() || '',
      max_groups: plan.max_groups?.toString() || '',
      max_contributions_per_month: plan.max_contributions_per_month?.toString() || '',
      features: featuresArray,
      reporting: features.reporting || 'basic',
      support: features.support || 'community',
      is_active: plan.is_active
    });
  };

  const resetForm = () => {
    setFormData({
      plan_name: '',
      plan_type: 'premium',
      price_vatu: 0,
      billing_model: 'flat',
      price_per_member_vatu: 0,
      max_group_members: '',
      max_groups: '',
      max_contributions_per_month: '',
      features: [''], // Pre-populate with one empty feature field
      reporting: 'basic',
      support: 'community',
      is_active: true
    });
  };

  const addFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, '']
    });
  };

  const updateFeature = (index: number, value: string) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({
      ...formData,
      features: newFeatures
    });
  };

  const removeFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading subscription plans...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Subscription Plan Management - communitySaving</title>
        <meta
          name="description"
          content="Manage subscription plans, pricing, and features for the community contribution platform"
        />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header
          onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          isMobileMenuOpen={isMobileMenuOpen}
        />

        <div className="flex">
          <div className="hidden lg:block">
            <Sidebar isCollapsed={isSidebarCollapsed} userRole="admin" isSuperAdmin={true} />
          </div>

          <main
            className={`flex-1 transition-all duration-300 ${
              isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
            }`}
          >
            <div className="p-4 lg:p-6 max-w-7xl mx-auto">
              <div className="mb-6">
                <BreadcrumbTrail />
              </div>

              <div className="flex items-center justify-between mb-6">
                <div>
                  <h1 className="text-2xl lg:text-3xl font-semibold text-foreground mb-2">
                    Subscription Plan Management
                  </h1>
                  <p className="text-muted-foreground">
                    Create, edit, and manage subscription tiers
                  </p>
                </div>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Create Plan
                </button>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-destructive/10 border border-destructive rounded-lg flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-destructive">{error}</p>
                  </div>
                </div>
              )}

              {/* Plans Table */}
              <div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plan Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Type</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Pricing</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Max Groups</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Max Members</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plans.map((plan) => (
                        <tr key={plan.id} className="border-t border-border hover:bg-muted/30">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <Package className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm font-medium text-foreground">{plan.plan_name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 capitalize">
                              {plan.plan_type}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-foreground">
                            {(plan.billing_model ?? 'flat') === 'per_member'
                              ? `${Number(plan.price_per_member_vatu ?? 0).toLocaleString()} VT / member / month`
                              : `${Number(plan.price_vatu ?? 0).toLocaleString()} VT / month`}
                          </td>
                          <td className="py-3 px-4 text-center text-sm text-foreground">
                            {plan.max_groups || 'Unlimited'}
                          </td>
                          <td className="py-3 px-4 text-center text-sm text-foreground">
                            {plan.max_group_members || 'Unlimited'}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              plan.is_active 
                                ? 'bg-green-100 text-green-800' :'bg-gray-100 text-gray-800'
                            }`}>
                              {plan.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleEdit(plan)}
                                className="p-2 hover:bg-muted rounded-md transition-colors"
                                title="Edit plan"
                              >
                                <Edit2 className="w-4 h-4 text-blue-600" />
                              </button>
                              <button
                                onClick={() => handleToggleActive(plan.id, plan.is_active)}
                                className="p-2 hover:bg-muted rounded-md transition-colors"
                                title={plan.is_active ? 'Deactivate' : 'Activate'}
                              >
                                <Power className={`w-4 h-4 ${plan.is_active ? 'text-yellow-600' : 'text-green-600'}`} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Create/Edit Modal */}
              {(showCreateModal || editingPlan) && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6 border-b border-border flex items-center justify-between">
                      <h2 className="text-xl font-semibold text-foreground">
                        {editingPlan ? 'Edit Subscription Plan' : 'Create Subscription Plan'}
                      </h2>
                      <button
                        onClick={() => {
                          setShowCreateModal(false);
                          setEditingPlan(null);
                          resetForm();
                        }}
                        className="p-2 hover:bg-muted rounded-md"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="p-6 space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Plan Name *
                        </label>
                        <input
                          type="text"
                          value={formData.plan_name}
                          onChange={(e) => setFormData({ ...formData, plan_name: e.target.value })}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                          placeholder="e.g., Premium Plan"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Plan Type *
                        </label>
                        <select
                          value={formData.plan_type}
                          onChange={(e) => setFormData({ ...formData, plan_type: e.target.value as any })}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                          disabled={!!editingPlan}
                        >
                          <option value="free">Free</option>
                          <option value="premium">Premium</option>
                          <option value="enterprise">Enterprise</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Pricing Model
                        </label>
                        <select
                          value={formData.billing_model}
                          onChange={(e) => setFormData({ ...formData, billing_model: e.target.value as any })}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                        >
                          <option value="flat">Flat (per month)</option>
                          <option value="per_member">Per member (per month)</option>
                        </select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Choose whether pricing is a fixed monthly fee or charged per group member.
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          {formData.billing_model === 'per_member' ? 'Price per member (VT) *' : 'Price per month (VT) *'}
                        </label>
                        <input
                          type="number"
                          value={formData.billing_model === 'per_member' ? formData.price_per_member_vatu : formData.price_vatu}
                          onChange={(e) => {
                            const n = parseFloat(e.target.value) || 0;
                            setFormData({
                              ...formData,
                              ...(formData.billing_model === 'per_member'
                                ? { price_per_member_vatu: n }
                                : { price_vatu: n }),
                            });
                          }}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                          placeholder={formData.billing_model === 'per_member' ? '30' : '5000'}
                          min={0}
                        />
                        {formData.billing_model === 'per_member' && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Total billed monthly = members in group × {Number(formData.price_per_member_vatu || 0).toLocaleString()} VT.
                          </p>
                        )}
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Max Groups
                          </label>
                          <input
                            type="number"
                            value={formData.max_groups}
                            onChange={(e) => setFormData({ ...formData, max_groups: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                            placeholder="Unlimited"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Max groups user can create
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Max Group Members
                          </label>
                          <input
                            type="number"
                            value={formData.max_group_members}
                            onChange={(e) => setFormData({ ...formData, max_group_members: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                            placeholder="Unlimited"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Max members per group
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1">
                            Max Contributions/Month
                          </label>
                          <input
                            type="number"
                            value={formData.max_contributions_per_month}
                            onChange={(e) => setFormData({ ...formData, max_contributions_per_month: e.target.value })}
                            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                            placeholder="Unlimited"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Max contributions per month
                          </p>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Reporting Level
                        </label>
                        <select
                          value={formData.reporting}
                          onChange={(e) => setFormData({ ...formData, reporting: e.target.value })}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                        >
                          <option value="basic">Basic</option>
                          <option value="advanced">Advanced</option>
                          <option value="premium">Premium</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Support Level
                        </label>
                        <select
                          value={formData.support}
                          onChange={(e) => setFormData({ ...formData, support: e.target.value })}
                          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
                        >
                          <option value="community">Community</option>
                          <option value="email">Email</option>
                          <option value="dedicated">Dedicated</option>
                        </select>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-foreground">
                            Features List *
                          </label>
                          <button
                            type="button"
                            onClick={addFeature}
                            className="text-sm text-primary hover:text-primary/80 flex items-center gap-1"
                          >
                            <Plus className="w-4 h-4" />
                            Add Feature
                          </button>
                        </div>
                        <div className="space-y-2">
                          {formData.features.map((feature, index) => (
                            <div key={index} className="flex gap-2">
                              <input
                                type="text"
                                value={feature}
                                onChange={(e) => updateFeature(index, e.target.value)}
                                className="flex-1 px-3 py-2 border border-border rounded-md bg-background text-foreground"
                                placeholder="e.g., Basic contribution tracking, Up to 5 group members"
                                autoFocus={index === formData.features.length - 1 && feature === ''}
                              />
                              {formData.features.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeFeature(index)}
                                  className="px-3 py-2 text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                                  title="Remove feature"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          These features will be displayed on the mobile app registration screen
                        </p>
                      </div>

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="is_active"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <label htmlFor="is_active" className="text-sm text-foreground">
                          Active Plan
                        </label>
                      </div>
                    </div>

                    <div className="p-6 border-t border-border flex items-center justify-end gap-3">
                      <button
                        onClick={() => {
                          setShowCreateModal(false);
                          setEditingPlan(null);
                          resetForm();
                        }}
                        className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={editingPlan ? handleUpdatePlan : handleCreatePlan}
                        className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
                      >
                        {editingPlan ? 'Update Plan' : 'Create Plan'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <button
                onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                className="hidden lg:flex fixed bottom-6 left-6 w-10 h-10 items-center justify-center bg-primary text-primary-foreground rounded-full shadow-lg hover:shadow-xl transition-shadow z-50"
                aria-label="Toggle sidebar"
              >
                <span className="text-sm font-medium">
                  {isSidebarCollapsed ? '→' : '←'}
                </span>
              </button>
            </div>
          </main>
        </div>
      </div>
    </>
  );
};

export default SubscriptionPlanManagement;